/** @file Export dialog body: the form, the schema check, one run at a time and its report (Worker export). */
import type { ExportResult } from '@pose2d/api/export/exportTypes';
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { useMemo, useState } from 'react';
import type { ProjectsApi } from '../../api/createProjectsApi';
import { errorText } from '../../shell/i18n/errorText';
import { Button } from '../../ui-kit/Button';
import { exportChoices } from './exportChoices';
import { DEFAULT_EXPORT_FORM, type ExportFormState } from './exportForm';
import { exportIssues } from './exportIssues';
import { ExportOptionsForm } from './ExportOptionsForm';
import { ExportReport } from './ExportReport';
import { toExportRequest } from './toExportRequest';

/** Props of ExportPanel. */
export interface ExportPanelProps {
  t: Translate;
  projectId: string;
  doc: Doc;
  client: Pick<ProjectsApi, 'runExport'>;
}

/** The run button stays off while the schema refuses the form; a new run clears the last report. */
export function ExportPanel({ t, projectId, doc, client }: ExportPanelProps) {
  const [form, setForm] = useState<ExportFormState>(DEFAULT_EXPORT_FORM);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const [busy, setBusy] = useState(false);
  const choices = useMemo(() => exportChoices(doc), [doc]);
  const request = toExportRequest(form);
  const issues = exportIssues(request);
  const run = (): void => {
    if (busy || issues.length > 0) return;
    setBusy(true);
    setResult(null);
    setFailure(null);
    client.runExport(projectId, request).then(setResult, (thrown: unknown) => { setFailure({ thrown }); }).finally(() => { setBusy(false); });
  };
  return (
    <div className="export-panel">
      <ExportOptionsForm t={t} form={form} onChange={setForm} choices={choices} />
      {issues.map((issue) => <p key={issue.path} className="shell-error">{t('exportDialog.invalid', { path: issue.path, message: issue.message })}</p>)}
      <Button label={t(busy ? 'exportDialog.running' : 'exportDialog.run')} primary disabled={busy || issues.length > 0} onClick={run} />
      {failure === null ? null : <p role="alert" className="shell-error">{errorText(t, failure.thrown)}</p>}
      {result === null ? null : <ExportReport t={t} result={result} />}
    </div>
  );
}
